import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package, Trash2 } from 'lucide-react';
import { get, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { money } from '@/lib/utils';
import { Alert, Badge, Button, Select, Spinner } from '@/components/ui/primitives';
import { packagePrice, type PackageQuote, type ServicePackage } from './PackagesTab';

export interface ChosenPackage {
  key: string;
  quote: PackageQuote;
}

export function AddPackagePanel({
  patientId,
  payerId,
  branchId,
  onAdd,
  onCancel,
}: {
  patientId: string;
  payerId?: string | null;
  branchId?: string | null;
  onAdd: (quote: PackageQuote) => void;
  onCancel: () => void;
}) {
  const [packageId, setPackageId] = useState('');

  const packages = useQuery({
    queryKey: ['packages', 'invoice_bundle'],
    queryFn: () => get<ServicePackage[]>(`/packages${qs({ kind: 'invoice_bundle', active: 1 })}`),
  });

  const quote = useQuery({
    queryKey: ['package-quote', packageId, patientId, payerId ?? null, branchId ?? null],
    queryFn: () => get<PackageQuote>(`/packages/${packageId}/quote${qs({ patientId: patientId || undefined, payerId: payerId || undefined, branchId: branchId || undefined })}`),
    enabled: Boolean(packageId),
    retry: false,
  });

  return (
    <div className="space-y-3 rounded-md border border-dashed p-3">
      <Select value={packageId} onChange={(e) => setPackageId(e.target.value)} disabled={packages.isLoading}>
        <option value="">{tr('billing', 'choose_a_package')}</option>
        {packages.data?.map((pkg) => (
          <option key={pkg.id} value={pkg.id}>
            {pkg.code} — {pkg.name} ({money(packagePrice(pkg))})
          </option>
        ))}
      </Select>
      {packages.data && packages.data.length === 0 && <p className="text-sm text-muted-foreground">{tr('billing', 'no_packages_to_bill_yet')}</p>}
      {packages.isError && <Alert variant="destructive">{describeError(packages.error)}</Alert>}

      {packageId && quote.isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> {tr('billing', 'quote')}
        </p>
      )}
      {quote.isError && <Alert variant="destructive">{describeError(quote.error)}</Alert>}
      {quote.data && (
        <>
          <PackageLines quote={quote.data} />
          <p className="text-xs text-muted-foreground">{tr('billing', 'the_quote_uses_the_patients_category_and_payer')}</p>
        </>
      )}

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {tr('billing', 'cancel')}
        </Button>
        <Button size="sm" disabled={!quote.data} onClick={() => quote.data && onAdd(quote.data)}>
          {tr('billing', 'add_to_bill')}
        </Button>
      </div>
    </div>
  );
}

export function PackageLines({ quote }: { quote: PackageQuote }) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <tbody>
          {quote.lines.map((line) => (
            <tr key={line.chargeItemId} className="border-b last:border-0">
              <td className="px-3 py-1.5 ps-6">
                {line.description}
                <span className="ms-2 text-xs text-muted-foreground">× {line.qty}</span>
              </td>
              <td className="px-3 py-1.5 text-end tabular text-muted-foreground">{line.discountMinor > 0 ? `− ${money(line.discountMinor)}` : ''}</td>
              <td className="px-3 py-1.5 text-end tabular">{money(line.netMinor)}</td>
            </tr>
          ))}
          {quote.packageDiscountMinor > 0 && (
            <tr className="border-t bg-muted/30 text-xs text-muted-foreground">
              <td className="px-3 py-1.5">{tr('billing', 'package_discount')}</td>
              <td className="px-3 py-1.5 text-end tabular" colSpan={2}>
                {money(quote.contentsMinor)} → {money(quote.priceMinor)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function ChosenPackageGroup({ chosen, onRemove }: { chosen: ChosenPackage; onRemove: () => void }) {
  const { quote } = chosen;
  return (
    <div className="space-y-2 rounded-md border bg-muted/20 p-2">
      <div className="flex items-center gap-2">
        <Package className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <p className="min-w-0 flex-1 truncate text-sm font-medium">{quote.name}</p>
        <Badge variant="secondary">{tr('billing', 'x_services', { count: quote.lines.length })}</Badge>
        <span className="tabular text-sm font-medium">{money(quote.totals.netMinor)}</span>
        <Button variant="ghost" size="icon" aria-label={tr('billing', 'remove_package')} onClick={onRemove}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <PackageLines quote={quote} />
    </div>
  );
}
